import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import classNames from 'classnames';
import type React from 'react';

import { LessonTitle } from '@/routes/_embaUserLayout/-components/LessonTitle';
import { generateScenarioFileUrl } from '@/utils/generateScenarioFileUrl';

import homeButton from './-assets/buttons/home.svg';
import nextButton from './-assets/buttons/next.svg';
import nextButtonDisabled from './-assets/buttons/next_disabled.svg';
import prevButton from './-assets/buttons/prev.svg';
import prevButtonDisabled from './-assets/buttons/prev_disabled.svg';
import { Background } from './-components/Background';
import styles from './index.module.scss';

import { Choice } from './-components/Content/Choice';
import { Column } from './-components/Content/Column';
import { Document } from './-components/Content/Document';
import { Normal } from './-components/Content/Normal';

import { Quiz } from './-components/Content/Quiz';
import { Transition } from './-components/Content/Transition';

export const COURSE_LESSON_SCENARIO_STORY_FRAGMENT = graphql(/* GraphQL */ `
  fragment EmbaCourseLessonScenarioStory on CourseLessonScenarioStory {
    id
    type
    background
    content {
      ...EmbaCourseLessonScenarioStoryNormal
      ...EmbaCourseLessonScenarioStoryDocument
      ...EmbaCourseLessonScenarioStoryChoice
      ...EmbaCourseLessonScenarioStoryQuiz
      ...EmbaCourseLessonScenarioStoryTransition
      ...EmbaCourseLessonScenarioStoryColumn
    }
  }
`);

export type CourseLessonScenarioPageProps = {
  currentStory: FragmentType<typeof COURSE_LESSON_SCENARIO_STORY_FRAGMENT>;
  chapterName: string;
  lessonName: string;
  embaUserlastName: string;
  isEnableNext: boolean;
  isEnablePrev: boolean;
  animationComplete: () => void;
  typingSpeed: number;
  showBlinkingArrow: boolean;
  showInteractions: boolean;
  showQuiz: boolean;
  isModalVisible: boolean;
  slideToHome: () => void;
  quizModalClose: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  handleSliderChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChoiceClick: (isCorrectChoice: boolean) => void;
  storyDataLength: number;
  currentStoryId: number;
  sliderRef: React.RefObject<HTMLDivElement>;
  slideToNext: () => void;
  slideToPrev: () => void;
  setCurrentStoryId: React.Dispatch<React.SetStateAction<number>>;
  setShowQuiz: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setShowInteractions: React.Dispatch<React.SetStateAction<boolean>>;
  setIsQuizCorrect: React.Dispatch<React.SetStateAction<boolean | null>>;
  isQuizCorrect: boolean | null;
};

export const CourseLessonScenarioPageComponent = ({
  currentStory,
  chapterName,
  lessonName,
  embaUserlastName,
  isEnableNext,
  isEnablePrev,
  animationComplete,
  typingSpeed,
  showBlinkingArrow,
  showInteractions,
  showQuiz,
  isModalVisible,
  slideToHome,
  quizModalClose,
  handleSliderChange,
  handleChoiceClick,
  storyDataLength,
  currentStoryId,
  sliderRef,
  slideToNext,
  slideToPrev,
  setCurrentStoryId,
  setShowQuiz,
  setIsModalVisible,
  setShowInteractions,
  isQuizCorrect,
  setIsQuizCorrect,
}: CourseLessonScenarioPageProps) => {
  const currentStoryData = useFragment(
    COURSE_LESSON_SCENARIO_STORY_FRAGMENT,
    currentStory,
  );

  const backGroundType =
    currentStoryData.content.__typename === 'ScenarioContentDocument' ||
    currentStoryData.content.__typename === 'ScenarioContentTransition'
      ? 'white'
      : null;

  return (
    <>
      <LessonTitle lessonName={lessonName} chapterName={chapterName} />
      <div className={styles.scenarioOuter}>
        <div className={styles.scenarioWrapper}>
          <Background
            src={generateScenarioFileUrl(
              currentStoryData.background || '',
              'background',
            )}
            type={backGroundType}
          >
            <div className={styles.storyWrapper}>
              {currentStoryData.content.__typename ===
                'ScenarioContentNormal' && (
                <Normal
                  currentStoryContent={currentStoryData.content}
                  embaUserlastName={embaUserlastName}
                  animationComplete={animationComplete}
                  typingSpeed={typingSpeed}
                  showBlinkingArrow={showBlinkingArrow}
                  isModalVisible={isModalVisible}
                  setIsModalVisible={setIsModalVisible}
                  slideToNext={slideToNext}
                  isEnableNext={isEnableNext}
                />
              )}
              {currentStoryData.content.__typename ===
                'ScenarioContentDocument' && (
                <Document
                  currentStoryContent={currentStoryData.content}
                  embaUserlastName={embaUserlastName}
                  animationComplete={animationComplete}
                  typingSpeed={typingSpeed}
                  showBlinkingArrow={showBlinkingArrow}
                  isModalVisible={isModalVisible}
                  setIsModalVisible={setIsModalVisible}
                  slideToNext={slideToNext}
                  isEnableNext={isEnableNext}
                />
              )}
              {currentStoryData.content.__typename ===
                'ScenarioContentChoice' && (
                <Choice
                  currentStoryContent={currentStoryData.content}
                  isEnableNext={isEnableNext}
                  animationComplete={animationComplete}
                  typingSpeed={typingSpeed}
                  showBlinkingArrow={showBlinkingArrow}
                  showInteractions={showInteractions}
                  slideToNext={slideToNext}
                  embaUserlastName={embaUserlastName}
                  setCurrentStoryId={setCurrentStoryId}
                  setShowInteractions={setShowInteractions}
                />
              )}
              {currentStoryData.content.__typename ===
                'ScenarioContentQuiz' && (
                <Quiz
                  currentStoryContent={currentStoryData.content}
                  isEnableNext={isEnableNext}
                  animationComplete={animationComplete}
                  typingSpeed={typingSpeed}
                  showBlinkingArrow={showBlinkingArrow}
                  showInteractions={showInteractions}
                  slideToNext={slideToNext}
                  embaUserlastName={embaUserlastName}
                  setShowInteractions={setShowInteractions}
                  setShowQuiz={setShowQuiz}
                  showQuiz={showQuiz}
                  isQuizCorrect={isQuizCorrect}
                  quizModalClose={quizModalClose}
                  setIsQuizCorrect={setIsQuizCorrect}
                  handleChoiceClick={handleChoiceClick}
                />
              )}
              {currentStoryData.content.__typename ===
                'ScenarioContentTransition' && (
                <Transition
                  currentStoryContent={currentStoryData.content}
                  isEnableNext={isEnableNext}
                  slideToNext={slideToNext}
                />
              )}
              {currentStoryData.content.__typename ===
                'ScenarioContentColumn' && (
                <Column
                  currentStoryContent={currentStoryData.content}
                  isEnableNext={isEnableNext}
                  slideToNext={slideToNext}
                />
              )}
              <div className={styles.controllArea}>
                <div className={styles.moveFirst}>
                  <img src={homeButton} alt="" onClick={() => slideToHome()} />
                </div>
                <div className={styles.sliderArea}>
                  <input
                    type="range"
                    min="1"
                    max={storyDataLength}
                    onChange={handleSliderChange}
                    className={styles.slider}
                    value={currentStoryId}
                    ref={sliderRef as React.RefObject<HTMLInputElement>}
                    onMouseUp={() => sliderRef.current?.blur()}
                  />
                  <p>
                    {currentStoryId} / {storyDataLength}
                  </p>
                </div>
                <div className={styles.moveButtonArea}>
                  <img
                    className={classNames(styles.moveButton, {
                      [styles.disabled]: !isEnablePrev,
                    })}
                    src={isEnablePrev ? prevButton : prevButtonDisabled}
                    alt=""
                    onClick={isEnablePrev ? () => slideToPrev() : undefined}
                  />
                  <img
                    className={classNames(styles.moveButton, {
                      [styles.disabled]: !isEnableNext,
                    })}
                    src={isEnableNext ? nextButton : nextButtonDisabled}
                    alt=""
                    onClick={isEnableNext ? () => slideToNext() : undefined}
                  />
                </div>
              </div>
            </div>
          </Background>
        </div>
      </div>
    </>
  );
};
